import { env } from './env.js'
import { friendlyError } from './errors.js'

/** Zod `flatten()` shape the API returns in `details` on VALIDATION_ERROR. */
export interface ValidationDetails {
  formErrors?: string[]
  fieldErrors?: Record<string, string[]>
}

export type ApiResult<T> =
  | { data: T }
  | {
      error: {
        code: string
        message: string
        serverMessage?: string
        details?: ValidationDetails
      }
    }

/**
 * Called whenever the API rejects a request with ACADEMY_SUSPENDED. Registered
 * at app bootstrap (main.tsx) to invalidate the `me` query so the suspended/
 * cancelled lockout screen engages immediately, even if `me` was cached as
 * active before suspension. Kept as a registered handler (not a direct import)
 * to avoid a circular dependency on the query client.
 */
let onAcademySuspended: (() => void) | null = null
export function setAcademySuspendedHandler(fn: () => void): void {
  onAcademySuspended = fn
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<ApiResult<T>> {
  const response = await fetch(`${env.API_URL}${path}`, {
    ...init,
    // Send the HttpOnly session cookie (scoped to .brightstars.in) cross-subdomain
    // to the API. Auth is cookie-based, not bearer-token based.
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init.headers,
    },
  })

  const body = (await response.json()) as ApiResult<T>

  if ('error' in body) {
    // A suspended/cancelled academy 403s here. Refetch `me` so the SPA flips to
    // the lockout screen instead of leaving the owner on stale dashboard data.
    if (body.error.code === 'ACADEMY_SUSPENDED') {
      onAcademySuspended?.()
    }
    // Normalize the displayed message through the shared error catalog so every
    // caller (most do `throw new Error(res.error.message)`) shows a consistent,
    // user-friendly line. The raw server message is preserved as serverMessage.
    const serverMessage = body.error.message
    body.error.serverMessage = serverMessage
    body.error.message = friendlyError({ code: body.error.code, message: serverMessage })
  }

  return body
}
